// Dashboard wording that has to stay true while the plan gate pauses
// delivery (review W31). Pure, so dashboard-copy.test.ts checks it without
// a DOM. Skipped deliveries are never re-sent (src/lib/billing-entitlement.ts),
// so no sentence here may promise that events from a pause go out later
// (review S9), no card may read as healthy while paused (review S8), and
// no card may send the merchant to the paywall banner for the fix: the
// banner comes from the app's once-per-load /status and may not be on
// screen (review R4).

export interface PaywallCopy {
  tone: "warning" | "critical";
  title: string;
  body: string;
  cta: string;
}

// Every paused body ends with this. "Resume" next to "still records" read as
// the recorded events going out after the fix (review S9).
const NOT_SENT_LATER = "Events from the paused days are not sent later.";

export function paywallCopy(reason: string): PaywallCopy {
  switch (reason) {
    case "free_period_ended":
      // Review W31: a store with no plan past its 30 free days. App shows
      // the end-of-period picker first; this is what the dashboard says
      // after "Not now".
      return {
        tone: "warning",
        title: "Your free period has ended",
        body: `Datacrow still records your store's events but has stopped sending them to your ad platforms. Pick a plan to start sending again. ${NOT_SENT_LATER}`,
        cta: "Choose a plan",
      };
    case "trial_expired":
      return {
        tone: "warning",
        title: "Your trial has ended",
        body: `Datacrow still records your store's events but delivery to your ad platforms is paused. Pick a plan and new events start sending again. ${NOT_SENT_LATER}`,
        cta: "Choose a plan",
      };
    case "cancelled_expired":
      return {
        tone: "warning",
        title: "Subscription ended",
        body: `Your billing period ended after the cancellation, so delivery to your ad platforms is paused. Re-subscribe and new events start sending again. ${NOT_SENT_LATER}`,
        cta: "Re-subscribe",
      };
    case "declined":
      return {
        tone: "critical",
        title: "Subscription declined in Shopify",
        body: `Your charge was declined on Shopify's side, so delivery is paused. Re-approve the subscription and new events start sending again. ${NOT_SENT_LATER}`,
        cta: "Re-subscribe",
      };
    case "frozen":
      return {
        tone: "critical",
        title: "Subscription frozen",
        body: `Shopify froze your subscription (typically a payment-method issue), so delivery is paused. Update billing in Shopify admin and new events start sending again. ${NOT_SENT_LATER}`,
        cta: "Manage billing",
      };
    case "expired":
      return {
        tone: "warning",
        title: "Subscription expired",
        body: `Your subscription has expired, so delivery to your ad platforms is paused. Re-subscribe and new events start sending again right away. ${NOT_SENT_LATER}`,
        cta: "Re-subscribe",
      };
    case "unknown_status":
    default:
      // Defensive: if the backend ever returns a reason we don't have copy
      // for, render a generic message rather than crashing. Surfacing this
      // to the merchant beats silent failure.
      return {
        tone: "warning",
        title: "Delivery paused",
        body: `Server-side delivery is paused while we resolve your subscription status. Open billing settings for details. ${NOT_SENT_LATER}`,
        cta: "Manage billing",
      };
  }
}

// Reasons a new plan lifts: no plan past the free period, or one that
// lapsed, was cancelled or was declined. Picking (or re-approving) a plan
// on Shopify's plan page is the fix for each.
const PICK_A_PLAN = new Set(["free_period_ended", "trial_expired", "cancelled_expired", "expired", "declined"]);

/**
 * "paused", plus what lifts it, from the gate's entitlement reason. Each
 * card that says "paused" carries its own fix (review R4) and never names
 * the wrong one: a frozen plan is fixed in Shopify admin's billing, not by
 * picking a plan (review S10). A reason with no known fix, or none (an
 * older backend mid-deploy), reads as plain "paused".
 */
export function pausedUntil(reason: string | null | undefined): string {
  if (reason === "frozen") return "paused until billing is fixed in Shopify admin";
  if (reason && PICK_A_PLAN.has(reason)) return "paused until you pick a plan";
  return "paused";
}

/**
 * The health card's "events flowing" row. While paused it says what lifts
 * the pause, from /api/dashboard/health's own pausedReason (review R4).
 */
export function eventsFlowingLabel(
  paused: boolean | undefined,
  pausedReason: string | null | undefined,
  deliveriesLastHour: number,
): string {
  return paused
    ? `Events flowing: ${pausedUntil(pausedReason)}`
    : `Events flowing (last hour): ${deliveriesLastHour}`;
}

/** The /api/dashboard/server-caught fields the card's wording depends on. */
export interface ServerCaughtCounts {
  totalOrders: number;
  missedOrders: number;
  /** Optional: an older backend mid-deploy doesn't send it. */
  missedUndelivered?: number;
}

export interface ServerCaughtSummary {
  /**
   * no_orders: nothing confirmed in the window. all_seen: the pixel saw
   * every order and delivery runs. caught: some pixel-missed orders were
   * delivered (the dollar headline). not_caught: none were, and `note` says
   * why.
   */
  kind: "no_orders" | "all_seen" | "caught" | "not_caught";
  /** The paused or not-delivered line, when there is one. */
  note: string | null;
}

/**
 * Pick the "Caught by the server" card's state. The all-clear ("your pixel
 * saw every order") shows only while delivery runs and no pixel-missed
 * order went unsent: a paused store's skipped orders never count as
 * caught, so without this check they read as an all-clear right under the
 * paywall banner (review S8). `pausedReason` is the gate's entitlement
 * reason, so the paused note names its own fix (review R4).
 */
export function serverCaughtSummary(
  data: ServerCaughtCounts,
  paused: boolean,
  pausedReason: string | null = null,
): ServerCaughtSummary {
  if (data.totalOrders === 0) return { kind: "no_orders", note: null };
  const undelivered = data.missedUndelivered ?? 0;
  const note = undeliveredNote(undelivered, paused, pausedReason);
  if (data.missedOrders > 0) return { kind: "caught", note };
  if (note === null) return { kind: "all_seen", note: null };
  return { kind: "not_caught", note };
}

// No sentence may promise the unsent orders go out later: skipped
// deliveries are final, and a failed one may never succeed.
function undeliveredNote(n: number, paused: boolean, pausedReason: string | null): string | null {
  const orders = n === 1 ? "1 order" : `${n.toLocaleString()} orders`;
  if (paused) {
    return n > 0
      ? `${orders} your pixel missed in the last 30 days ${n === 1 ? "wasn't" : "weren't"} sent while delivery is paused.`
      : `Delivery is ${pausedUntil(pausedReason)}, so orders your browser pixel misses are not sent to your ad platforms.`;
  }
  return n > 0
    ? `${orders} your pixel missed in the last 30 days ${n === 1 ? "hasn't" : "haven't"} reached your ad platforms. Recent activity shows why.`
    : null;
}

export interface ConnectMoreCard {
  title: string;
  body: string;
}

/**
 * The "connect more destinations" nudge, or null when there is nothing left
 * to connect.
 *
 * `available` is how many destination types THIS shop may use, which is not
 * the size of the catalog. Five types shipped at release "hidden" on
 * 2026-10-18 and are filtered out for every shop that is not a preview shop,
 * so a catalog-length denominator told ordinary merchants they had connected
 * "6 of 11" against a picker that only ever offered six. Pass the length of
 * allTypesFrom(availableTypes), never DESTINATION_TYPES.length.
 */
export function connectMoreCard(configured: number, available: number): ConnectMoreCard | null {
  // Clamped: a destination configured before its type went hidden still
  // counts as configured, so a preview shop that loses access to a type it
  // already set up would otherwise read "7 of 6".
  if (configured >= available) return null;
  return {
    title: `${configured} of ${available} destinations connected`,
    body: "Connect more to maximize your ad + analytics coverage.",
  };
}
